import { formatCurrency } from '@crm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Modal,
  Spinner,
} from '@crm/ui';
import {
  Check,
  CircleDot,
  FileText,
  ListFilter,
  Minus,
  Plus,
  ScanSearch,
  WalletCards,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { BarChart, PageHeader, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type BusinessXrayCategory, useBusinessXray, useSaasPlans } from '../api';

const CATEGORIES: { key: BusinessXrayCategory; label: string }[] = [
  { key: 'LEADS_NOVOS', label: 'Leads novos' },
  { key: 'LEADS_CONFIGURANDO', label: 'Leads configurando' },
  { key: 'LEADS_COMPLETOS', label: 'Leads com configuracao completa' },
  { key: 'ADIMPLENTES', label: 'Assinantes adimplentes' },
  { key: 'INADIMPLENTES', label: 'Assinantes inadimplentes' },
];

const PIE_COLORS = ['#0f766e', '#dc2626', '#2563eb', '#d97706', '#7c3aed'];

function DistributionPie({ categories }: { categories: { label: string; count: number }[] }) {
  const total = categories.reduce((sum, category) => sum + category.count, 0);
  if (!total)
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">Nenhum perfil no recorte.</p>
    );

  let start = 0;
  const stops = categories.map((category, index) => {
    const end = start + (category.count / total) * 100;
    const stop = `${PIE_COLORS[index % PIE_COLORS.length]} ${start}% ${end}%`;
    start = end;
    return stop;
  });

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-center">
      <div
        aria-label="Distribuição dos assinantes por perfil"
        className="h-44 w-44 shrink-0 rounded-full"
        role="img"
        style={{ background: `conic-gradient(${stops.join(', ')})` }}
      >
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-background text-center shadow-sm">
            <span className="text-xl font-semibold">{total}</span>
          </div>
        </div>
      </div>
      <ul className="w-full space-y-2 text-sm sm:max-w-xs">
        {categories.map((category, index) => (
          <li key={category.label} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
              />
              <span className="truncate">{category.label}</span>
            </span>
            <span className="shrink-0 tabular-nums text-foreground">
              {category.count} ({Math.round((category.count / total) * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function XrayModal({
  open,
  onClose,
  onGenerated,
  processing,
  processingStep,
}: {
  open: boolean;
  onClose: () => void;
  onGenerated: (input: {
    categories: BusinessXrayCategory[];
    planIds: string[];
    months: number;
  }) => void;
  processing: boolean;
  processingStep: number;
}) {
  const plans = useSaasPlans();
  const [categories, setCategories] = useState<BusinessXrayCategory[]>(
    CATEGORIES.map((category) => category.key),
  );
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [months, setMonths] = useState('1');

  const toggle = (key: BusinessXrayCategory) =>
    setCategories((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  const togglePlan = (id: string) =>
    setPlanIds((current) => {
      const all = plans.data?.data.map((plan) => plan.id) ?? [];
      const selected = current.length === 0 ? all : current;
      const next = selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id];

      return next.length === all.length ? [] : next;
    });

  const monthsNumber = Number(months);
  const valid = categories.length > 0 && Number.isInteger(monthsNumber) && monthsNumber >= 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtros do Raio-X"
      subtitle="Escolha os recortes para estimar receita e entender a base."
      size="large"
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: 'Gerar relatorio',
          icon: <ScanSearch className="h-4 w-4" />,
          disabled: !valid || processing,
          loading: processing,
          onClick: () => onGenerated({ categories, planIds, months: monthsNumber }),
        },
      ]}
    >
      {processing ? (
        <div className="space-y-5 py-6">
          <div className="space-y-2 text-center">
            <Spinner className="mx-auto h-8 w-8 text-primary" />
            <h3 className="font-medium">Gerando seu relatório</h3>
            <p className="text-sm text-muted-foreground">Processando os dados selecionados.</p>
          </div>
          <ol className="mx-auto max-w-md space-y-3">
            {[
              'Preparando filtros',
              'Consultando a base de clientes e leads',
              'Calculando a estimativa por plano',
            ].map((step, index) => (
              <li
                key={step}
                className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${
                  index === processingStep
                    ? 'border-primary/40 bg-primary/5 font-medium'
                    : 'border-border'
                }`}
              >
                {index < processingStep ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : index === processingStep ? (
                  <Spinner className="h-4 w-4 text-primary" />
                ) : (
                  <CircleDot className="h-4 w-4 text-muted-foreground" />
                )}
                {step}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="space-y-5">
          <fieldset className="space-y-3">
            <legend className="font-medium">Perfis da base</legend>
            <p className="text-sm text-muted-foreground">Selecione um ou mais grupos.</p>
            <div className="grid gap-3 rounded-card border border-border p-4 sm:grid-cols-2">
              {CATEGORIES.map((category) => (
                <Checkbox
                  key={category.key}
                  label={category.label}
                  checked={categories.includes(category.key)}
                  onChange={() => toggle(category.key)}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-medium">Planos</legend>
            <p className="text-sm text-muted-foreground">
              Todos selecionados incluem leads sem plano.
            </p>
            <div className="grid gap-3 rounded-card border border-border p-4 sm:grid-cols-2">
              {plans.isLoading ? (
                <Spinner className="h-5 w-5 text-muted-foreground" />
              ) : (
                plans.data?.data.map((plan) => (
                  <Checkbox
                    key={plan.id}
                    label={plan.name}
                    checked={planIds.length === 0 || planIds.includes(plan.id)}
                    onChange={() => togglePlan(plan.id)}
                  />
                ))
              )}
            </div>
          </fieldset>
        </div>
      )}

      <div className="mt-6 space-y-2">
        <p className="mb-2 text-sm font-medium">Meses a estimar</p>
        <div className="flex w-full items-center gap-2 sm:max-w-xs">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Diminuir meses"
            disabled={monthsNumber <= 1 || processing}
            onClick={() => setMonths(String(Math.max(1, monthsNumber - 1)))}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <label className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-md border border-input bg-background px-3 text-sm">
            <input
              aria-label="Meses a estimar"
              className="min-w-0 w-16 bg-transparent text-center outline-none"
              type="number"
              min="1"
              max="60"
              value={months}
              disabled={processing}
              onChange={(event) => setMonths(event.target.value)}
            />
            <span className="text-muted-foreground">meses</span>
          </label>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Aumentar meses"
            disabled={monthsNumber >= 60 || processing}
            onClick={() => setMonths(String(Math.min(60, monthsNumber + 1)))}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          A promoção vale nos primeiros meses; depois a conta usa o valor normal do plano.
        </p>
      </div>
    </Modal>
  );
}

function Report({
  report,
  input,
  planDetails,
  onNewReport,
}: {
  report: NonNullable<ReturnType<typeof useBusinessXray>['data']>;
  input: { categories: BusinessXrayCategory[]; planIds: string[]; months: number };
  planDetails: Map<string, { name: string; monthlyPrice: number }>;
  onNewReport: () => void;
}) {
  const planData = report.plans.map((plan) => ({
    label: plan.name,
    values: { estimated: plan.estimatedRevenue },
    tooltip: [
      `Plano: ${plan.name}`,
      `Assinantes: ${plan.count}`,
      `Valor do plano: ${formatCurrency(planDetails.get(plan.id)?.monthlyPrice ?? 0)}`,
      `Meses: ${input.months}`,
      `Total: ${formatCurrency(plan.estimatedRevenue)}`,
    ].join('\n'),
  }));

  return (
    <div className="space-y-4">
      <Card variant="flush">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <ListFilter className="h-4 w-4" />
              </span>
              <div>
                <h2 className="font-semibold">Filtros aplicados</h2>
                <p className="text-sm text-muted-foreground">Resumo do recorte deste relatório</p>
              </div>
            </div>
            <Button type="button" variant="ghost" onClick={onNewReport}>
              <ScanSearch className="h-4 w-4" />
              Editar filtros
            </Button>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-3">
            <dl className="grid divide-y divide-border text-sm sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-3 py-3 sm:px-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Perfis
                </dt>
                <dd className="mt-2 flex flex-wrap gap-1.5">
                  {input.categories
                    .map((key) => CATEGORIES.find((category) => category.key === key)?.label)
                    .map((label) => (
                      <span
                        key={label}
                        className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                      >
                        {label}
                      </span>
                    ))}
                </dd>
              </div>
              <div className="px-3 py-3 sm:px-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Planos
                </dt>
                <dd className="mt-2 flex flex-wrap gap-1.5">
                  {(input.planIds.length === 0
                    ? ['Todos, incluindo sem plano']
                    : input.planIds.map((id) => planDetails.get(id)?.name ?? id)
                  ).map((label) => (
                    <span
                      key={label}
                      className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                    >
                      {label}
                    </span>
                  ))}
                </dd>
              </div>
              <div className="px-3 py-3 sm:px-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Período
                </dt>
                <dd className="mt-2 text-lg font-semibold tabular-nums">
                  {input.months} {input.months === 1 ? 'mês' : 'meses'}
                </dd>
              </div>
            </dl>
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard title="Total Geral de Assinantes" value={report.total} icon={<CircleDot />} />
        <StatCard
          title="Total Geral Estimado"
          value={formatCurrency(report.estimatedRevenue)}
          icon={<WalletCards />}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card variant="flush">
          <CardHeader>
            <CardTitle className="text-base">Distribuicao por perfil</CardTitle>
          </CardHeader>
          <CardContent>
            <DistributionPie categories={report.categories} />
          </CardContent>
        </Card>
        <Card variant="flush">
          <CardHeader>
            <CardTitle className="text-base">Receita estimada por plano</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={planData}
              series={[{ key: 'estimated', label: 'Estimativa', color: 'bg-emerald-500' }]}
              formatValue={formatCurrency}
              drawWhenEmpty
            />
          </CardContent>
        </Card>
      </div>

      <Card variant="flush">
        <CardHeader>
          <CardTitle className="text-base">Detalhamento</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {report.categories.map((category) => (
              <li
                key={category.key}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <span className="text-base font-medium text-slate-600 dark:text-slate-300">
                  {category.label}
                </span>
                <span className="text-sm text-foreground">
                  {category.count} assinantes · {formatCurrency(category.estimatedRevenue)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export function SaasBusinessXrayPage() {
  useDocumentTitle('Relatório de Assinaturas');
  const [modalOpen, setModalOpen] = useState(true);
  const [lastInput, setLastInput] = useState<{
    categories: BusinessXrayCategory[];
    planIds: string[];
    months: number;
  } | null>(null);
  const [processingStep, setProcessingStep] = useState(0);
  const relatorio = useBusinessXray();
  const plans = useSaasPlans();

  useEffect(() => {
    if (!relatorio.isPending) {
      setProcessingStep(0);
      return;
    }

    const timer = window.setInterval(() => setProcessingStep((step) => Math.min(step + 1, 2)), 700);
    return () => window.clearInterval(timer);
  }, [relatorio.isPending]);

  const gerar = (input: {
    categories: BusinessXrayCategory[];
    planIds: string[];
    months: number;
  }) => {
    setLastInput(input);
    relatorio.mutate(input, { onSuccess: () => setModalOpen(false) });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FileText />}
        title="Relatório de Assinaturas"
        description="Estimativa da base por perfil e plano, considerando assinantes, valor mensal e quantidade de pagamentos."
      />
      {relatorio.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(relatorio.error)}</p>
      ) : null}
      {relatorio.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : null}
      {relatorio.data && lastInput ? (
        <Report
          report={relatorio.data}
          input={lastInput}
          planDetails={
            new Map(
              plans.data?.data.map((plan) => [
                plan.id,
                { name: plan.name, monthlyPrice: plan.monthlyPrice },
              ]) ?? [],
            )
          }
          onNewReport={() => setModalOpen(true)}
        />
      ) : (
        <Card variant="flush">
          <CardContent className="py-12 text-center text-muted-foreground">
            Gere um recorte para visualizar o relatório.
          </CardContent>
        </Card>
      )}
      <XrayModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onGenerated={gerar}
        processing={relatorio.isPending}
        processingStep={processingStep}
      />
    </div>
  );
}
